/**
 * Project Service
 * Handles project-related database operations
 */

import { COLLECTIONS, SCHEMAS } from '../config.js'

import { BaseService } from './BaseService.js'
import { http } from '@/utils/httpClient'

export class ProjectService extends BaseService {
  constructor() {
    super(COLLECTIONS.PROJECTS)
  }

  /**
   * Create a new project
   * @param {Object} projectData - Project data
   * @returns {Promise<Object>} Created project
   */
  async createProject(projectData) {
    const validation = this.validateData(projectData, SCHEMAS[COLLECTIONS.PROJECTS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    // Set default status if not provided
    if (!projectData.status) {
      projectData.status = 'active'
    }

    return await this.create(projectData)
  }

  /**
   * Update project
   * @param {string} projectId - Project ID
   * @param {Object} updateData - Update data
   * @returns {Promise<Object>} Updated project
   */
  async updateProject(projectId, updateData) {
    const validation = this.validateData(updateData, SCHEMAS[COLLECTIONS.PROJECTS])
    if (!validation.isValid) {
      throw new Error(`Validation failed: ${validation.errors.join(', ')}`)
    }

    return await this.update(projectId, updateData)
  }

  /**
   * Get projects by team
   * @param {string} teamId - Team ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of projects
   */
  async getByTeam(teamId, _options = {}) {
    // Backend now handles ?teamId= filtering directly (GET /projects?teamId=:id)
    await this._ensureDatabaseService()
    const results = await (this as any).db.adapter.makeRequest(`/projects?teamId=${encodeURIComponent(teamId)}`)
    return Array.isArray(results) ? results : []
  }

  /**
   * Get projects by creator
   * @param {string} userId - User ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of projects
   */
  async getByCreator(userId, options = {}) {
    return await this.findBy('created_by', userId, options)
  }

  /**
   * Get projects by status
   * @param {string} status - Project status
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of projects
   */
  async getByStatus(status, options = {}) {
    return await this.findBy('status', status, options)
  }

  /**
   * Get active projects
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of active projects
   */
  async getActive(options = {}) {
    return await this.getByStatus('active', options)
  }

  /**
   * Archive project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  async archive(projectId) {
    return await this.update(projectId, { status: 'archived' })
  }

  /**
   * Activate project
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Updated project
   */
  async activate(projectId) {
    return await this.update(projectId, { status: 'active' })
  }

  /**
   * Search projects
   * @param {string} searchTerm - Search term
   * @param {Object} options - Search options
   * @returns {Promise<Array>} Array of matching projects
   */
  async searchProjects(searchTerm, options = {}) {
    const searchOptions = {
      fields: ['name', 'description'],
      ...options
    }
    return await this.search(searchTerm, searchOptions)
  }

  /**
   * Get project statistics
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project statistics
   */
  async getStatistics(projectId) {
    const project = await this.getById(projectId)
    if (!project) {
      throw new Error('Project not found')
    }

    // This would typically involve other services to get counts
    // For now, return basic project info
    return {
      id: project.id,
      name: project.name,
      status: project.status,
      teamId: project.teamId,
      createdBy: project.created_by,
      createdAt: project.created_at,
      lastUpdated: new Date()
    }
  }

  /**
   * Get lightweight dashboard stats — projects with denormalized counts + global totals.
   * Single API call replaces fetching all entities to compute counts.
   */
  async getDashboardStats() {
    await this._ensureDatabaseService()
    return await (this as any).db.adapter.makeRequest('/projects/dashboard-stats', {
      method: 'GET'
    })
  }

  /**
   * Get project by ID with populated team (same payload shape as GET ?include=team).
   */
  async getByIdWithTeam(projectId: string) {
    await this._ensureDatabaseService()
    return await (this as any).db.adapter.makeRequest(`/projects/${projectId}?include=team`, {
      method: 'GET'
    })
  }

  /**
   * Get pre-computed project overview statistics.
   * Backend returns counts and breakdowns — no full entity arrays.
   * @param {string} projectId - Project ID
   * @returns {Promise<Object>} Project data with overview stats
   */
  async getOverviewStatistics(projectId) {
    await this._ensureDatabaseService()

    return await (this as any).db.adapter.makeRequest(`/projects/${projectId}?include=overview`, {
      method: 'GET'
    })
  }

  /**
   * Get resolved project members for project-scoped assignee pickers.
   * Falls back to the backend's team-member resolution for legacy projects.
   * @param {string} projectId - Project ID
   * @returns {Promise<Array>} Array of project members
   */
  async getMembers(projectId) {
    await this._ensureDatabaseService()

    try {
      const members = await (this as any).db.adapter.makeRequest(`/projects/${projectId}/members`, {
        method: 'GET'
      })

      return Array.isArray(members) ? members : []
    } catch (error) {
      console.error('❌ Error fetching project members:', error)
      throw error
    }
  }

  /**
   * Get all project statistics
   * @returns {Promise<Object>} All projects statistics
   */
  async getAllStatistics() {
    const projects = await this.getAll()
    const statusCounts = {}
    const teamCounts = {}

    projects.forEach(project => {
      // Count by status
      statusCounts[project.status] = (statusCounts[project.status] || 0) + 1

      // Count by team
      teamCounts[project.teamId] = (teamCounts[project.teamId] || 0) + 1
    })

    return {
      total: projects.length,
      statusCounts,
      teamCounts,
      lastUpdated: new Date()
    }
  }

  /**
   * Get projects by user access
   * @param {string} userId - User ID
   * @param {string} teamId - Team ID (optional)
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of accessible projects
   */
  async getByUserAccess(userId, teamId = null, options = {}) {
    if (teamId) {
      // Get projects for specific team
      return await this.getByTeam(teamId, options)
    } else {
      // Get all projects (in a real app, this would be filtered by user permissions)
      return await this.getAll(options)
    }
  }

  /**
   * Soft delete a project and cascade to all its resources
   * @param {string} projectId - Project ID
   * @param {string} deletedBy - User ID who deleted the project
   * @param {Object} options - Deletion options
   * @param {boolean} options.cascadeToResources - Whether to cascade delete to resources (default: true)
   * @returns {Promise<Object>} Deletion summary
   */
  async softDelete(projectId, deletedBy, options: any = {}) {
    const { cascadeToResources = true } = options
    
    
    // Get the project first
    const project = await this.getById(projectId)
    if (!project) {
      throw new Error('Project not found')
    }
    
    const summary = {
      projectId,
      projectName: project.name,
      deletedAt: new Date(),
      deletedBy,
      resourcesDeleted: {
        features: 0,
        testCases: 0,
        testRuns: 0,
        bugs: 0,
        folders: 0
      }
    }
    
    // Use single batch operation for project + all resources
    if (cascadeToResources) {
      const cascadeSummary = await this._cascadeDeleteResources(projectId, deletedBy)
      summary.resourcesDeleted = cascadeSummary.resourcesDeleted
    } else {
      // Just delete the project if cascade is disabled.
      // Hit the dedicated DELETE endpoint directly so we go through
      // projects.service.remove() server-side, which sets BOTH is_deleted=true
      // and deleted_at. BaseService.delete() routes through HttpAdapter.delete()
      // which only PATCHes deleted_at — and the projects update endpoint strips
      // is_deleted anyway — leaving a partial-deleted state that can't be
      // restored (restore() gates on project.is_deleted).
      await http.delete(`/projects/${projectId}`)
    }
    
    return summary
    
  }

  /**
   * Cascade delete resources for a project (internal method)
   * @param {string} projectId - Project ID
   * @param {string} deletedBy - User ID who deleted the project
   * @returns {Promise<Object>} Deletion summary
   */
  async _cascadeDeleteResources(projectId, deletedBy) {
    const summary = {
      resourcesDeleted: {
        features: 0,
        testCases: 0,
        testRuns: 0,
        bugs: 0,
        folders: 0
      }
    }
    
    try {
      // Import all required services
      const { 
        featureService, 
        testCaseService, 
        testRunService, 
        bugService,
        folderService 
      } = await import('./index.js')
      
      // Fetch all resources for the project
      const [features, testCases, testRuns, bugs, folders] = await Promise.all([
        featureService.getByProject(projectId),
        testCaseService.findBy('project_id', projectId),
        testRunService.findBy('project_id', projectId),
        bugService.findBy('project_id', projectId),
        folderService.getByProject(projectId)
      ])
      
      // Filter out already deleted items
      const activeFeatures = features.filter(f => !f.is_deleted)
      const activeTestCases = testCases.filter(tc => !tc.is_deleted)
      const activeTestRuns = testRuns.filter(tr => !tr.is_deleted)
      const activeBugs = bugs.filter(b => !b.is_deleted)
      const activeFolders = folders.filter(f => !f.is_deleted)
      
      // Prepare batch operations for project + all resources
      const allOperations = []

      // 0. Soft delete the project itself in the same batch.
      // Use 'delete' (not 'update') because the projects.update() endpoint
      // strips is_deleted/deleted_at/deleted_by from the payload as a guard
      // against accidental deletion. The 'delete' op routes to remove(),
      // which performs the actual soft-delete.
      allOperations.push({
        type: 'delete',
        collection: 'projects',
        id: projectId
      })
      
      // 1-5. Soft delete all child resources.
      // Use 'delete' (routes to service.remove()) instead of 'update'.
      // Every resource's update() endpoint whitelists fields and silently
      // strips is_deleted/deleted_at/deleted_by — so 'update' batches reported
      // success while leaving rows untouched (folders.service.ts:201,
      // projects.service.ts:1136). 'delete' goes through remove() which
      // performs the actual soft-delete server-side.
      activeFeatures.forEach(feature => {
        allOperations.push({ type: 'delete', collection: 'features', id: feature.id })
      })

      activeTestCases.forEach(testCase => {
        allOperations.push({ type: 'delete', collection: 'test_cases', id: testCase.id })
      })

      activeTestRuns.forEach(testRun => {
        allOperations.push({ type: 'delete', collection: 'test_runs', id: testRun.id })
      })

      activeBugs.forEach(bug => {
        allOperations.push({ type: 'delete', collection: 'bugs', id: bug.id })
      })

      activeFolders.forEach(folder => {
        allOperations.push({ type: 'delete', collection: 'folders', id: folder.id })
      })
      
      // Execute all operations in a single batch call
      if (allOperations.length > 0) {
        await projectService.batch(allOperations)
      }
      
      // Update summary counts
      summary.resourcesDeleted.features = activeFeatures.length
      summary.resourcesDeleted.testCases = activeTestCases.length
      summary.resourcesDeleted.testRuns = activeTestRuns.length
      summary.resourcesDeleted.bugs = activeBugs.length
      summary.resourcesDeleted.folders = activeFolders.length
      
      return summary

    } catch (error) {
      // Surface the failure to the caller so the UI doesn't show a fake success
      // toast and the project stays in the list (we hit this exact issue
      // before — silent swallow let "deleted" projects reappear on reload).
      throw error
    }
  }

  /**
   * Restore a soft-deleted project
   * @param {string} projectId - Project ID
   * @param {string} restoredBy - User ID who restored the project
   * @param {Object} options - Restoration options
   * @param {boolean} options.restoreResources - Whether to restore resources (default: true)
   * @returns {Promise<Object>} Restoration summary
   */
  async restore(projectId, restoredBy, options: any = {}) {
    const { restoreResources = true } = options
    
    
    // Get the project first
    const project = await this.getById(projectId)
    if (!project) {
      throw new Error('Project not found')
    }
    
    if (!project.is_deleted) {
      throw new Error('Project is not deleted')
    }
    
    const summary = {
      projectId,
      projectName: project.name,
      restoredAt: new Date(),
      restoredBy,
      resourcesRestored: {
        features: 0,
        testCases: 0,
        testRuns: 0,
        bugs: 0,
        folders: 0
      }
    }
    
    // Optionally restore all resources using single batch
    if (restoreResources) {
      
      // Import all required services
      const { 
        featureService, 
        testCaseService, 
        testRunService, 
        bugService,
        folderService 
      } = await import('./index.js')
      
      // Fetch all resources for the project
      // Note: folderService.getAllByProject includes deleted folders;
      // getByProject only returns active ones and would miss deleted folders.
      const [allFeatures, allTestCases, allTestRuns, allBugs, allFolders] = await Promise.all([
        featureService.findBy('project_id', projectId),
        testCaseService.findBy('project_id', projectId),
        testRunService.findBy('project_id', projectId),
        bugService.findBy('project_id', projectId),
        folderService.getAllByProject(projectId)
      ])

      // Filter for deleted items only
      const deletedFeatures = allFeatures.filter(f => f.is_deleted)
      const deletedTestCases = allTestCases.filter(tc => tc.is_deleted)
      const deletedTestRuns = allTestRuns.filter(tr => tr.is_deleted)
      const deletedBugs = allBugs.filter(b => b.is_deleted)
      const deletedFolders = allFolders.filter(f => f.is_deleted)
      
      // Prepare batch operations for project + all resources
      const allOperations = []
      
      // 0. Restore the project itself in the same batch
      allOperations.push({
        type: 'update',
        collection: 'projects',
        id: projectId,
        data: {
          is_deleted: false,
          deleted_at: null,
          deleted_by: null,
          updated_at: new Date()
        }
      })
      
      // 1. Restore all features
      deletedFeatures.forEach(feature => {
        allOperations.push({
          type: 'update',
          collection: 'features',
          id: feature.id,
          data: {
            is_deleted: false,
            deleted_at: null,
            deleted_by: null,
            updated_at: new Date()
          }
        })
      })
      
      // 2. Restore all test cases
      deletedTestCases.forEach(testCase => {
        allOperations.push({
          type: 'update',
          collection: 'test_cases',
          id: testCase.id,
          data: {
            is_deleted: false,
            deleted_at: null,
            deleted_by: null,
            updated_at: new Date()
          }
        })
      })
      
      // 3. Restore all test runs
      deletedTestRuns.forEach(testRun => {
        allOperations.push({
          type: 'update',
          collection: 'test_runs',
          id: testRun.id,
          data: {
            is_deleted: false,
            deleted_at: null,
            deleted_by: null,
            updated_at: new Date()
          }
        })
      })
      
      // 4. Restore all bugs
      deletedBugs.forEach(bug => {
        allOperations.push({
          type: 'update',
          collection: 'bugs',
          id: bug.id,
          data: {
            is_deleted: false,
            deleted_at: null,
            deleted_by: null,
            updated_at: new Date()
          }
        })
      })
      
      // 5. Restore all folders
      deletedFolders.forEach(folder => {
        allOperations.push({
          type: 'update',
          collection: 'folders',
          id: folder.id,
          data: {
            is_deleted: false,
            deleted_at: null,
            deleted_by: null,
            updated_at: new Date()
          }
        })
      })
      
      // Execute all operations in a single batch call
      if (allOperations.length > 0) {
        await projectService.batch(allOperations)
      }
      
      // Update summary counts
      summary.resourcesRestored.features = deletedFeatures.length
      summary.resourcesRestored.testCases = deletedTestCases.length
      summary.resourcesRestored.testRuns = deletedTestRuns.length
      summary.resourcesRestored.bugs = deletedBugs.length
      summary.resourcesRestored.folders = deletedFolders.length
    }
    
    return summary
    
  }

  /**
   * Get deleted projects
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of deleted projects
   */
  async getDeleted(options = {}) {
    const allProjects = await this.getAll(options)
    return allProjects.filter(project => project.is_deleted === true)
  }

  /**
   * Get deleted projects by team
   * @param {string} teamId - Team ID
   * @param {Object} options - Query options
   * @returns {Promise<Array>} Array of deleted projects
   */
  async getDeletedByTeam(teamId, options = {}) {
    const allProjects = await this.getByTeam(teamId, options)
    return allProjects.filter(project => project.is_deleted === true)
  }
}

export const projectService = new ProjectService()
